import { apiRequest } from '../lib/httpClient'
import type {
  McpCatalog,
  McpCatalogEntry,
  McpCatalogValues,
  McpPlaybook,
  McpPlaybookInput,
  McpPlaybookScope,
  McpPlaybookStep,
  McpPlaybookSummary,
  McpSavePlaybookRequest,
} from '@/types/mcp'
import { asArray, pickBool, pickString } from './shared/coercion'

const SCOPES: readonly McpPlaybookScope[] = ['starter', 'firmEdit', 'firm', 'personal']

const asObject = (raw: unknown) => (raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null)
const asRecord = (raw: unknown) => asObject(raw) ?? {}

const strings = (raw: unknown) => asArray<unknown>(raw).map(pickString)

function toInput(raw: unknown): McpPlaybookInput {
  const record = asRecord(raw)
  return {
    name: pickString(record['name']),
    description: pickString(record['description']),
    workedOut: pickBool(record['workedOut']),
  }
}

/** Steps saved before 2026-10-20 are strings, and a step saved with a `visual` reads as its text. */
function toStep(raw: unknown): McpPlaybookStep {
  if (typeof raw === 'string') return { text: raw }
  const record = asRecord(raw)
  const id = pickString(record['id'])
  const query = asObject(record['query'])
  const step = { ...(id ? { id } : {}), text: pickString(record['text']) }
  return query ? { ...step, query } : step
}

function toScope(raw: unknown): McpPlaybookScope {
  const scope = pickString(raw)
  return SCOPES.find((known) => known.toLowerCase() === scope.toLowerCase()) ?? 'personal'
}

function toSummary(raw: unknown): McpPlaybookSummary {
  const record = asRecord(raw)
  return {
    id: pickString(record['id']),
    name: pickString(record['name']),
    when: pickString(record['when']),
    description: pickString(record['description']),
    scope: toScope(record['scope']),
    inputs: asArray<unknown>(record['inputs']).map(toInput).filter((input) => input.name),
    adminOnly: pickBool(record['adminOnly']),
    canEdit: pickBool(record['canEdit']),
    editedBy: pickString(record['editedBy']),
    updatedAt: pickString(record['updatedAt']) || null,
  }
}

function toPlaybook(raw: unknown): McpPlaybook {
  const record = asRecord(raw)
  return {
    ...toSummary(raw),
    starterKey: pickString(record['starterKey']) || null,
    steps: asArray<unknown>(record['steps']).map(toStep),
  }
}

function toCatalogEntry(raw: unknown): McpCatalogEntry {
  const record = asRecord(raw)
  return {
    id: pickString(record['id']),
    label: pickString(record['label']),
    type: pickString(record['type']),
    section: pickString(record['section']),
    description: pickString(record['description']),
    synonyms: strings(record['synonyms']),
  }
}

const toEntries = (raw: unknown) => asArray<unknown>(raw).map(toCatalogEntry).filter((entry) => entry.id)

const playbookPath = (id: string) => `Mcp/playbooks/${encodeURIComponent(id)}`

export async function fetchMcpPlaybooks(clientId: string): Promise<McpPlaybookSummary[]> {
  const payload = await apiRequest<unknown>('Mcp/playbooks', { method: 'GET', query: { clientId } })
  return asArray<unknown>(payload)
    .map(toSummary)
    .filter((playbook) => playbook.id)
}

export async function fetchMcpPlaybook(clientId: string, id: string): Promise<McpPlaybook> {
  return toPlaybook(await apiRequest<unknown>(playbookPath(id), { method: 'GET', query: { clientId } }))
}

/** `confirmed` is always sent: the user has seen what they're saving, so there is no describe-only pass. */
export async function saveMcpPlaybook(request: McpSavePlaybookRequest): Promise<McpPlaybook> {
  const record = asRecord(
    await apiRequest<unknown>('Mcp/playbooks', { method: 'POST', body: { ...request, confirmed: true } })
  )
  return toPlaybook(record['playbook'])
}

export async function deleteMcpPlaybook(clientId: string, id: string): Promise<void> {
  await apiRequest<boolean>(playbookPath(id), { method: 'DELETE', query: { clientId } })
}

export async function fetchMcpCatalog(clientId: string): Promise<McpCatalog> {
  const record = asRecord(await apiRequest<unknown>('Mcp/catalog', { method: 'GET', query: { clientId } }))
  return {
    catalogVersion: pickString(record['catalogVersion']),
    measures: toEntries(record['measures']),
    dimensions: toEntries(record['dimensions']),
  }
}

export async function fetchMcpCatalogValues(clientId: string, dimension: string, search: string): Promise<McpCatalogValues> {
  const record = asRecord(
    await apiRequest<unknown>('Mcp/catalog/values', { method: 'GET', query: { clientId, dimension, search } })
  )
  return { values: strings(record['values']).filter(Boolean), truncated: pickBool(record['truncated']) }
}
